import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import clsx from 'clsx';

import authLayout800 from '@assets/images/auth-layout/auth_layout-800w.webp';
import authLayout1200 from '@assets/images/auth-layout/auth_layout-1200w.webp';
import authLayout1600 from '@assets/images/auth-layout/auth_layout-1600w.webp';
import authLayout2400 from '@assets/images/auth-layout/auth_layout-2400w.webp';
import authLayoutOptimized from '@assets/images/auth-layout/auth_layout-optimized.webp';
import { Icon, Spinner } from '@components';
import { AUTH } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import StepFooter from '@features/authentication/components/step-footer/StepFooter';
import {
  StepFooterProvider,
  useStepFooterContext
} from '@features/authentication/components/step-footer/StepFooterContext';
import { useLazyAppConfigQuery } from '@features/authentication/queries/AuthQuery';
import { setAppConfig } from '@layouts/main-header/userSlice';
import MobileLayout from '@layouts/mobile-layout/MobileLayout';
import { LogoutModal } from '@layouts/side-nav-bar/components';
import { getLocalStorageItem } from '@utils/storage';

import AccessGuard from '../access-guard/AccessGuard';

const AuthLayoutContent = () => {
  const { pathname } = useLocation();
  const { footer } = useStepFooterContext();
  const isCategoryPage = pathname === AUTH.SELECT_CATEGORY;
  const showLogout =
    pathname !== AUTH.LOGIN && !!getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  useEffect(() => {
    if (!showLogout) setIsLogoutModalOpen(false);
  }, [showLogout]);

  return (
    <>
      <div className="z-100 hidden h-screen w-screen overflow-hidden mobile:flex">
        <MobileLayout />
      </div>
      <div className="flex h-screen items-center mobile:hidden">
        <section className="relative flex h-screen w-full items-center justify-center bg-content py-8 sm:w-1/3 md:w-1/2">
          <picture className="flex size-full items-center justify-center">
            <source
              srcSet={`${authLayout800} 800w, ${authLayout1200} 1200w, ${authLayout1600} 1600w, ${authLayout2400} 2400w`}
              sizes="(max-width: 640px) 33vw, (max-width: 768px) 50vw, 100vw"
              type="image/webp"
            />
            <img
              src={authLayoutOptimized}
              srcSet={`${authLayout800} 800w, ${authLayout1200} 1200w, ${authLayout1600} 1600w, ${authLayout2400} 2400w`}
              sizes="(max-width: 640px) 33vw, (max-width: 768px) 50vw, 100vw"
              alt="login-layout-pic"
              className="max-h-full w-full object-contain"
              fetchPriority="low"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </section>
        <section className="relative flex h-dvh w-full flex-col sm:w-2/3 md:w-1/2">
          {showLogout && (
            <button
              type="button"
              onClick={() => setIsLogoutModalOpen(true)}
              className="absolute right-10 top-10 z-10 flex items-center gap-1.5 text-sm font-medium text-cofeePrimary6"
            >
              <Icon name="logout-icon" size="none" className="size-5" />
              Log out
            </button>
          )}
          <div className="flex flex-1 items-center justify-center overflow-y-auto p-10">
            <div
              className={clsx(
                'm-6 flex flex-col justify-start',
                isCategoryPage ? 'w-139' : 'w-105'
              )}
            >
              <Suspense
                fallback={
                  <div className="flex size-full items-center justify-center">
                    <Spinner />
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </div>
          </div>
          {footer && <StepFooter {...footer} />}
        </section>
      </div>
      {showLogout && isLogoutModalOpen && (
        <LogoutModal
          isOpen={isLogoutModalOpen}
          onCancelLogout={() => setIsLogoutModalOpen(false)}
        />
      )}
    </>
  );
};

const AuthLayout = () => {
  const dispatch = useDispatch();
  const { appConfig } = useSelector((state: any) => state.user);

  const [getAppConfig] = useLazyAppConfigQuery();

  useEffect(() => {
    // Load whenever missing (initial mount, or logout cleared user state
    // while AuthLayout stayed mounted). Defer so first paint isn't blocked.
    if (appConfig) return undefined;

    let cancelled = false;
    let idleCallbackId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const loadAppConfig = async () => {
      const resp = await getAppConfig(null);
      if (!cancelled && 'data' in resp) {
        dispatch(setAppConfig(resp.data));
      }
    };

    if ('requestIdleCallback' in window) {
      idleCallbackId = requestIdleCallback(loadAppConfig, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(loadAppConfig, 100);
    }

    return () => {
      cancelled = true;
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [appConfig, dispatch, getAppConfig]);

  return (
    <AccessGuard>
      <StepFooterProvider>
        <AuthLayoutContent />
      </StepFooterProvider>
    </AccessGuard>
  );
};

export default AuthLayout;
